/* eslint-disable indent, no-param-reassign,no-plusplus, no-unused-vars */
import axios from 'axios';
import Vue from 'vue';
import differenceBy from 'lodash/differenceBy';
import cacheAxios from '../../util/cacheAxios';

const newsTaggerAxios = axios.create();
const cache = new Map();
cacheAxios(newsTaggerAxios, cache);
newsTaggerAxios.interceptors.response.use(response => response,
  (error) => {
    if (error && error.response && error.response.status === 401) {
      setTimeout(() => {
        window.location.href = '/sign/in?source=/aims#/newstagger';
      }, 500);
    }
  });

function getDateString(date) {
  if (!date) return '';
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

const pastDate = new Date();
pastDate.setDate(pastDate.getDate() - 30);

export default {
  state: {
    keywords: [],
    dateFromTagged: pastDate,
    dateToTagged: new Date(),
    dateFromUntagged: pastDate,
    dateToUntagged: new Date(),
    untaggedArticles: [],
    hrArticles: [],
    bizArticles: [],
    cities: [],
  },
  mutations: {
    setUntaggedArticles(state, data) {
      state.untaggedArticles = data;
    },
    setHrArticles(state, data) {
      state.hrArticles = data;
    },
    addHrArticle(state, article) {
      state.hrArticles.push(article);
    },
    setBizArticles(state, data) {
      state.bizArticles = data;
    },
    updateBizArticle(state, article) {
      const i = state.bizArticles.findIndex(a => a.link === article.link);
      if (i >= 0) {
        Vue.set(state.bizArticles[i], 'sentiment_score', article.sentiment_score);
        Vue.set(state.bizArticles[i], 'countries', article.countries);
      }
    },
    updateHrArticle(state, article) {
      const i = state.hrArticles.findIndex(a => a.link === article.link);
      if (i >= 0) {
        Vue.set(state.hrArticles[i], 'sentiment_score', article.sentiment_score);
        Vue.set(state.hrArticles[i], 'category', article.category);
        Vue.set(state.hrArticles[i], 'country', article.country);
        Vue.set(state.hrArticles[i], 'legislationArticleType', article.legislationArticleType);
        Vue.set(state.hrArticles[i], 'legislationDateToImplement', article.legislationDateToImplement);
      }
    },
    removeUntaggedArticles(state, articles) {
      state.untaggedArticles = differenceBy(state.untaggedArticles, articles, 'link');
    },
    removeHrArticles(state, articles) {
      state.hrArticles = differenceBy(state.hrArticles, articles, 'link');
    },
    setHrKeywords(state, data) {
      state.keywords = data;
    },
    removeHrKeyword(state, keyword) {
      state.keywords = state.keywords.filter(k => !(k.keyword === keyword.keyword && k.category ===
        keyword.category));
    },
    addHrKeyword(state, keyword) {
      state.keywords.push(keyword);
    },
    dateFromUntaggedChanged(state, date) {
      state.dateFromUntagged = date;
    },
    dateToUntaggedChanged(state, date) {
      state.dateToUntagged = date;
    },
    dateFromTaggedChanged(state, date) {
      state.dateFromTagged = date;
    },
    dateToTaggedChanged(state, date) {
      state.dateToTagged = date;
    },
    setCities(state, cities) {
      state.cities = cities;
    },
  },
  actions: {
    initHrKeywords({
      commit,
    }) {
      newsTaggerAxios.get('/aims/api/newsTagger/keywords')
        .then((res) => {
          if (res.data) {
            commit('setHrKeywords', res.data);
          }
        });
    },
    getUntaggedNews({
      commit,
      state,
    }) {
      newsTaggerAxios.post('/aims/api/newsTagger/untaggedNews', {
          from: getDateString(state.dateFromUntagged),
          to: getDateString(state.dateToUntagged),
        })
        .then((res) => {
          if (res.data) {
            commit('setUntaggedArticles', res.data.map((s) => {
              s.published = s.published.substr(0, 10);
              return s;
            }));
          }
        });
    },
    getHrNewsForTagging({
      commit,
      state,
    }) {
      newsTaggerAxios.post('/aims/api/newsTagger/hrNews', {
          from: getDateString(state.dateFromTagged),
          to: getDateString(state.dateToTagged),
        })
        .then((res) => {
          if (res.data) {
            commit('setHrArticles', res.data.map((s) => {
              s.published = s.published.substr(0, 10);
              return s;
            }));
          }
        });
    },
    getBizNewsForTagging({
      commit,
      state,
    }) {
      newsTaggerAxios.post('/aims/api/newsTagger/bizNews', {
          from: getDateString(state.dateFromTagged),
          to: getDateString(state.dateToTagged),
        })
        .then((res) => {
          if (res.data) {
            commit('setBizArticles', res.data.map((s) => {
              s.published = s.published.substr(0, 10);
              return s;
            }));
          }
        });
    },
    getAllCities({
      commit,
      state,
    }) {
      newsTaggerAxios.get('/aims/api/newsTagger/cities')
        .then((res) => {
          if (res.data) {
            commit('setCities', res.data);
          }
        });
    },
    deleteKeyword({
      commit,
    }, keyword) {
      newsTaggerAxios.post('/aims/api/newsTagger/removeTag', keyword)
        .then((res) => {
          if (res && res.status === 200) {
            commit('removeHrKeyword', keyword);
          }
        });
    },
    deleteHrArticles({
      commit,
    }, articles) {
      return newsTaggerAxios.post('/aims/api/newsTagger/deleteHrArticles', articles)
        .then((res) => {
          if (res && res.status === 200) {
            commit('removeHrArticles', articles);
            return Promise.resolve();
          }
          return Promise.reject('Failed saving articles');
        })
        .catch(err => Promise.reject(err));
    },
    addKeyword({
      commit,
    }, keyword) {
      return newsTaggerAxios.post('/aims/api/newsTagger/addTag', keyword)
        .then((res) => {
          if (res && res.status === 200) {
            commit('addHrKeyword', keyword);
            return Promise.resolve();
          }
          return Promise.reject(
            `Cannot save "${keyword.keyword}". Check that it does not already exist.`);
        })
        .catch(err => Promise.reject(err));
    },
    saveUntaggedArticles({
      commit,
    }, articles) {
      return newsTaggerAxios.post('/aims/api/newsTagger/updateArticles', articles)
        .then((res) => {
          if (res && res.status === 200) {
            commit('removeUntaggedArticles', articles);
            return Promise.resolve();
          }
          return Promise.reject('Failed saving articles');
        })
        .catch(err => Promise.reject(err));
    },
    saveBizArticles({
      commit,
    }, article) {
      return newsTaggerAxios.post('/aims/api/newsTagger/updateBizArticle', article)
        .then((res) => {
          if (res && res.status === 200) {
            commit('updateBizArticle', article);
            return Promise.resolve();
          }
          return Promise.reject('Failed saving article');
        })
        .catch(err => Promise.reject(err));
    },
    saveHrArticles({
      commit,
    }, article) {
      return newsTaggerAxios.post('/aims/api/newsTagger/updateHrArticle', article)
        .then((res) => {
          if (res && res.status === 200) {
            commit('updateHrArticle', article);
            return Promise.resolve();
          }
          return Promise.reject('Failed saving article');
        })
        .catch(err => Promise.reject(err));
    },
    insertHrArticle({
      commit,
    }, article) {
      return newsTaggerAxios.post('/aims/api/newsTagger/insertHrArticle', article)
        .then((res) => {
          if (res && res.status === 200) {
            commit('addHrArticle', article);
            return Promise.resolve();
          }
          return Promise.reject('Failed saving article. Check that article do not already exist.');
        })
        .catch(err => Promise.reject(err));
    },
  },
  getters: {},
};



// WEBPACK FOOTER //
// ./src/store/modules/newsTaggerModule.js